/**
 * Die gesprochene Antwort über den Strom-Weg. (23.09.2026, Ticket 2448)
 *
 * ## Der Anlass
 *
 * Martin, 21.09.: Der Antworttext steht längst da, die Stimme kommt erst
 * deutlich später. Seit pre.3105 gibt es für sein Konto den Durchreich-Weg
 * („Vorlesen sofort (Beta)") — er hing aber nur an GET /api/tts/stream. Die
 * Antwort-Warteschlange holte jedes Stück weiter per POST /api/voice-preview
 * als fertige Datei (`res.blob()`), und die rechnet Soniox erst ganz aus.
 * Journal 21.–23.09., uid 1: 131 erste Stücke, Median 2.870 ms bis
 * abspielbereit — genau wie vor der Beta (2.802 ms). 2.282 Zeilen
 * „[voice-preview] soniox", alle gepuffert.
 *
 * ## Was hier passiert
 *
 * Bei eingeschalteter Beta und einer Soniox-Stimme liefert die Warteschlange
 * eine /api/tts/stream-Adresse statt einer Datei; das Audio-Element spielt,
 * während Soniox noch spricht. Alles andere bleibt auf dem alten Weg.
 *
 * ⚠️ Scheitert die Vorbereitung, kommt `null` — dann holt der Aufrufer das
 *    Stück wie bisher als Datei. Nur ein Abbruch wird weitergereicht.
 */

/**
 * ⚠️⚠️ 25.09.2026 — DER STROM-WEG STOTTERT. Für die Antworten ist er aus.
 *
 * Martin: *„Das Vorlesen ist gerade echt wieder sehr mühselig. Es stoppt,
 * stottert."* Journal 22.–25.09., uid 1, je Stück Tonlänge gegen echte
 * Spieldauer:
 *
 *     Datei-Weg (fertig gerechnet)      1.320 Stücke, 3 gestört
 *     Strom, schon vorgeladen             513 Stücke, 0 gestört
 *     Strom, erstes Stück der Antwort      86 Stücke, 71 gestört
 *                                         (193 s Stocken bei 252 s Ton)
 *     Strom, später live angespielt       115 Stücke, 38 gestört
 *
 * Soniox rechnet ungefähr in Echtzeit (siehe server/datei-vertonung.ts);
 * wer abspielt, während es noch rechnet, holt es über das Mobilnetz ein
 * und stockt. Der Zeitgewinn am Anfang ging im Stocken wieder verloren.
 *
 * Zurückdrehen erst, wenn eine Messung dieser Art für den Strom-Weg
 * weniger als 5 % gestörte erste Stücke zeigt.
 */
export const STROM_WEG_FUER_ANTWORTEN = false;

/** Nimmt diese Antwort den Strom-Weg? */
export function stromWegFuer(o: {
  beta: boolean;
  voiceId: string;
  /** Vorlese-Tempo aus den Einstellungen. */
  tempo?: number;
}): boolean {
  if (!STROM_WEG_FUER_ANTWORTEN) return false;
  if (!o.beta) return false;
  if (!o.voiceId.startsWith("soniox:")) return false;
  /*
   * ⚠️ Der Strom-Weg kennt kein Tempo — /api/voice-preview dehnt per atempo
   *    (server/loudness.ts, mp3Tempo). Wer schneller hört, bleibt auf dem
   *    alten Weg, statt still in normaler Geschwindigkeit zu hören.
   */
  return o.tempo === undefined || o.tempo === 1;
}

/**
 * Das Token für einen Satz der Antwort-Warteschlange — oder null, wenn der
 * Server keins gibt (leerer Text, Fehler). (02.10.2026, Ticket 2919: aus
 * `stromAdresse` herausgezogen, der PCM-Weg braucht dieselbe Anfrage.)
 *
 * `satz: true` sagt dem Server: das ist ein Stück aus der Warteschlange —
 * dieselbe Säuberung wie der Stimmen-Weg (Reste zerteilter Auszeichnung).
 * `voiceSource: "user"`: die angefragte Stimme gilt, wie bei
 * /api/voice-preview (#522 v2). `tempo` geht nur mit, wenn eins gesetzt ist —
 * ohne bleibt der Körper Zeichen für Zeichen wie vor Ticket 2919.
 */
export async function tokenFuerSatz(
  text: string,
  voiceId: string,
  signal: AbortSignal,
  holen: typeof fetch = fetch,
  tempo?: number,
  /** 17.10.2026: der Schmied im Ganz-Modus lädt vor — ohne Vorrang vor Vesperas Stücken (server/soniox-schleuse.ts, Spur `vorab`). */
  vorab?: boolean,
): Promise<string | null> {
  try {
    const r = await holen("/api/tts/prepare", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        text,
        voiceId,
        voiceSource: "user",
        satz: true,
        ...(tempo !== undefined ? { tempo } : {}),
        ...(vorab ? { vorab: true } : {}),
      }),
      signal,
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { token?: unknown };
    return typeof j.token === "string" && j.token ? j.token : null;
  } catch (err) {
    if (signal.aborted) throw err;
    return null;
  }
}

/**
 * Die Strom-Adresse für einen Satz — oder null, wenn der Weg nicht geht.
 * Scheitert die Vorbereitung, holt der Aufrufer das Stück wie bisher als Datei.
 */
export async function stromAdresse(
  text: string,
  voiceId: string,
  signal: AbortSignal,
  holen: typeof fetch = fetch,
  vorab?: boolean,
): Promise<string | null> {
  const token = await tokenFuerSatz(text, voiceId, signal, holen, undefined, vorab);
  return token ? `/api/tts/stream?token=${encodeURIComponent(token)}` : null;
}
